import { useEffect, useState } from "react";
import { MdLocationOn } from "react-icons/md";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioLocalEstoque } from "../../sync/repositories/almoxarifado";

const AJUDA = {
  titulo: "Cadastro de Locais de Estoque",
  descricao:
    "Local de Estoque representa onde os itens ficam guardados fisicamente (depósito, galpão, silo, etc.). Esse cadastro é usado nos Acertos de Estoque e aparece nas Movimentações para indicar de onde a quantidade sai ou entra.",
  secoes: [
    {
      titulo: "Dados do Local",
      campos: [
        {
          nome: "Nome do Local",
          obrigatorio: true,
          explicacao:
            "Nome que identifica o local fisicamente (ex.: 'Depósito Central', 'Galpão Norte', 'Silo 2').",
        },
        {
          nome: "Descrição",
          obrigatorio: false,
          explicacao:
            "Detalhes complementares sobre o local, como capacidade, localização dentro da fazenda ou observações de uso.",
        },
      ],
    },
  ],
  dicas: [
    "Excluir um local com acertos ou movimentações vinculadas não é permitido pelo servidor — verifique se não há registros associados antes de tentar excluir.",
  ],
};

export default function GestaoLocaisEstoque() {
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [locais, setLocais] = useState([]);
  const [locaisFiltrados, setLocaisFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
  });

  useEffect(() => {
    carregarLocais();
  }, []);

  const carregarLocais = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioLocalEstoque.listar();

      const dados = response.data.results || response.data;
      setLocais(dados);
      setLocaisFiltrados(dados);
    } catch (error) {
      console.error("Erro ao carregar locais de estoque:", error);
      alert("Erro ao carregar lista de locais de estoque");
    } finally {
      setCarregandoLista(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      alert("Preencha o nome do local de estoque.");
      return;
    }
    
    setLoading(true);

    try {
      if (editando) {
        // Editar local existente
        const response = await repositorioLocalEstoque.atualizar(editandoId, formData);
        if (response.status === 200) {
          alert("Local de estoque atualizado com sucesso!");
          handleCancel();
          carregarLocais();
        }
      } else {
        // Cadastrar novo local
        const response = await repositorioLocalEstoque.criar(formData);
        if (response.status === 201 || response.status === 200) {
          alert("Local de estoque cadastrado com sucesso!");
          handleCancel();
          carregarLocais();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (local) => {
    setFormData({
      nome: local.nome || "",
      descricao: local.descricao || "",
    });
    setEditando(true);
    setEditandoId(local.id);
    setMostrarFormulario(true);
    
    // Scroll para o topo para ver o formulário
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoLocal = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      descricao: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este local de estoque? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioLocalEstoque.excluir(id);
      alert("Local de estoque excluído com sucesso!");
      carregarLocais();
      
      // Se estava editando este local, limpar formulário
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir local de estoque. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...locais];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(l => 
        l.nome?.toLowerCase().includes(buscaLower) ||
        l.id?.toString().includes(buscaLower) ||
        l.descricao?.toLowerCase().includes(buscaLower)
      );
    }
    
    setLocaisFiltrados(filtrados);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setLocaisFiltrados(locais);
  };

  // Truncar descrição para exibição
  const truncarDescricao = (descricao, maxLength = 60) => {
    if (!descricao) return "—";
    if (descricao.length <= maxLength) return descricao;
    return descricao.substring(0, maxLength) + "...";
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Locais de Estoque <MdLocationOn size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoLocal}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Local de Estoque
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Local de Estoque" : "Cadastrar Novo Local de Estoque"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="flex flex-col w-96">
                  <label className="text-sm font-medium text-gray-700 mb-1">Nome do Local *</label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Ex: Depósito Central, Galpão Norte, Silos..."
                    required
                  />
                </div>

                <div className="flex flex-col w-full max-w-xl">
                  <label className="text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea
                    name="descricao"
                    rows={4}
                    value={formData.descricao}
                    onChange={handleChange}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Detalhes sobre o local (capacidade, localização, observações)..."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Código, Nome ou Descrição</label>
                  <input
                    type="text"
                    placeholder="Digite o código, nome ou descrição do local..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Locais de Estoque */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {locaisFiltrados.length} local(is) de estoque encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locaisFiltrados.map((local) => (
                        <tr key={local.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{local.id}</td>
                          <td className="p-3 text-sm font-medium">{local.nome}</td>
                          <td className="p-3 text-sm max-w-md">{truncarDescricao(local.descricao)}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(local)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(local.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                           </td>
                         </tr>
                      ))}
                      
                      {locaisFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhum local de estoque encontrado com os filtros selecionados."
                              : "Nenhum local de estoque cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}